import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Sparkles, X, Volume2, Hourglass } from "lucide-react";

export const supportsPip = () => typeof window !== "undefined" && "documentPictureInPicture" in window;

const copyStyles = (target) => {
  [...document.styleSheets].forEach((sheet) => {
    try {
      if (sheet.href) {
        const link = target.document.createElement("link");
        link.rel = "stylesheet";
        link.href = sheet.href;
        target.document.head.appendChild(link);
      } else {
        const style = target.document.createElement("style");
        style.textContent = [...sheet.cssRules].map((r) => r.cssText).join("\n");
        target.document.head.appendChild(style);
      }
    } catch (e) {
      /* cross-origin sheet: skip */
    }
  });
};

export const useFloatingCoach = () => {
  const [pipWindow, setPipWindow] = useState(null);
  const [inlineOpen, setInlineOpen] = useState(false);
  const containerRef = useRef(null);

  const open = useCallback(async () => {
    if (!supportsPip()) {
      setInlineOpen(true);
      return;
    }
    try {
      const w = await window.documentPictureInPicture.requestWindow({ width: 440, height: 380 });
      copyStyles(w);
      w.document.body.className = "coach-pip-body";
      const root = w.document.createElement("div");
      root.id = "coach-root";
      w.document.body.appendChild(root);
      containerRef.current = root;
      w.addEventListener("pagehide", () => {
        containerRef.current = null;
        setPipWindow(null);
      });
      setPipWindow(w);
    } catch (e) {
      setInlineOpen(true);
    }
  }, []);

  const close = useCallback(() => {
    pipWindow?.close();
    setInlineOpen(false);
  }, [pipWindow]);

  useEffect(() => () => pipWindow?.close(), [pipWindow]);

  return { open, close, isOpen: !!pipWindow || inlineOpen, isPip: !!pipWindow, container: containerRef.current, inlineOpen };
};

export const CoachListening = ({ testPrefix }) => (
  <div data-testid={`${testPrefix}-listening`} className="space-y-3 py-2">
    <p className="flex items-center gap-2 text-sm font-medium text-emerald-200"><span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> In ascolto</p>
    <p className="text-sm leading-relaxed text-muted-foreground">Preparo l’azione e la frase. Le mostro quando il cliente sembra aver concluso, dopo una breve pausa.</p>
  </div>
);

export const CoachAdvice = ({ suggestion, testPrefix, streaming = false }) => (
  <div className="space-y-4">
    <div>
      <p className="mb-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Azione</p>
      <p data-testid={`${testPrefix}-concept`} className="text-xl font-bold leading-tight text-[#FCD990]">{suggestion.concetto || "Rispondi al cliente"}</p>
    </div>
    <div>
      <p className="mb-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Puoi dire</p>
      <blockquote data-testid={streaming ? `${testPrefix}-stream-text` : testPrefix === "coach" ? "coach-suggestion-text" : "suggestion-text"} className="rounded-r-lg border-l-2 border-primary/60 bg-white/[0.04] px-3 py-2.5 text-base font-medium leading-relaxed text-white">
        {suggestion.testo_suggerimento}{streaming && <span className="animate-pulse text-[#FCD990]"> ▍</span>}
      </blockquote>
    </div>
  </div>
);

export const CoachProgress = ({ draft, testPrefix }) => (
  <div data-testid={`${testPrefix}-progress`} className="space-y-3">
    {draft ? <CoachAdvice suggestion={draft} testPrefix={testPrefix} streaming /> : <p className="text-sm text-muted-foreground">Preparo azione e frase…</p>}
    <p data-testid={`${testPrefix}-stream-status`} className="flex items-center gap-2 text-[11px] text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Completo il suggerimento…</p>
  </div>
);

export const CoachPanel = ({ suggestion, pending, draft, coachState, listening, onClose, compact }) => {
  const showDraft = !!draft;
  const showSuggestion = !showDraft && !!suggestion;
  const waiting = !showDraft && !showSuggestion && listening && coachState !== "ready";
  const showPending = !showDraft && !showSuggestion && !waiting && !!pending;
  const red = showSuggestion && suggestion.testo_suggerimento && suggestion.azione === "rispondi";
  const ringClass = waiting ? "coach-ring--green" : showDraft || showPending ? "coach-ring--amber" : red ? "coach-ring--red" : "coach-ring--green";
  return (
    <div data-testid="floating-coach" className={`coach-ring ${ringClass} ${compact ? "" : "coach-inline"}`}>
      <div className="coach-inner flex h-full flex-col gap-3 p-4">
        <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
          <span className="inline-flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-[#F5B942]" /> SalesPailot · Coach live</span>
          <span className="inline-flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${listening ? "pulse-dot bg-emerald-400" : "bg-gray-500"}`} />
            {onClose && <button data-testid="floating-coach-close" onClick={onClose} className="rounded p-0.5 hover:text-white"><X className="h-3.5 w-3.5" /></button>}
          </span>
        </div>
        {showSuggestion && suggestion.testo_suggerimento && <div data-testid="coach-action" className={`inline-flex w-fit items-center gap-2 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${red ? "bg-red-500/15 text-red-200" : "bg-emerald-500/15 text-emerald-200"}`}>
          {red ? <Volume2 className="h-3 w-3" /> : <Hourglass className="h-3 w-3" />}
          {red ? "Puoi intervenire" : "Lascia spazio"}
        </div>}
        <div className="flex-1 overflow-y-auto">
          {waiting ? <CoachListening testPrefix="coach" /> : showDraft || showPending ? (
            <CoachProgress draft={draft} testPrefix="coach" />
          ) : !suggestion ? (
            <p className="text-sm text-muted-foreground">Avvia l'ascolto per ricevere azioni e frasi pronte.</p>
          ) : suggestion.testo_suggerimento ? (
            <CoachAdvice suggestion={suggestion} testPrefix="coach" />
          ) : (
            <p className="text-sm text-muted-foreground">Nessun suggerimento pertinente nella tua Knowledge Base per questa frase.</p>
          )}
        </div>
        {showSuggestion && suggestion.trigger_text && <p className="truncate text-[11px] italic text-muted-foreground">Cliente: “{suggestion.trigger_text}”</p>}
      </div>
    </div>
  );
};

export const FloatingCoach = ({ coach, ...panelProps }) => {
  if (coach.isPip && coach.container) return createPortal(<CoachPanel {...panelProps} compact onClose={coach.close} />, coach.container);
  if (coach.inlineOpen) {
    return (
      <div data-testid="floating-coach-inline" className="fixed bottom-20 right-4 z-[80] w-[min(92vw,420px)] lg:bottom-6">
        <CoachPanel {...panelProps} onClose={coach.close} />
      </div>
    );
  }
  return null;
};
